import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Card, CardContent, TextField, Button, Typography, Box, Alert,
  CircularProgress, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';

const PASSWORD_RE = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const { t } = useLanguage();
  const theme = useTheme();

  const passwordValid = PASSWORD_RE.test(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordValid) {
      setError(t.passwordRequirements);
      return;
    }
    setLoading(true);
    setError('');

    try {
      await api.post('/register', {
        name,
        date_of_birth: dateOfBirth,
        gender,
        height: parseFloat(height),
        email,
        password,
        weight_history: [{ date: new Date().toISOString().split('T')[0], weight: parseFloat(weight) }],
        target_weight: parseFloat(targetWeight) || 0,
        target_date: targetDate || new Date().toISOString().split('T')[0],
      });
      // Registration returns a pending-approval message — no token is issued
      setPending(true);
    } catch {
      setError(t.registerFailed);
    } finally {
      setLoading(false);
    }
  };

  if (pending) {
    return (
      <Container maxWidth="xs" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
        <Box sx={{ width: '100%' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">{t.registrationPendingTitle}</Typography>
            <Typography variant="body2">{t.registrationPendingBody}</Typography>
          </Alert>
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>
              {t.logIn}
            </Link>
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {t.appName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.createYourAccount}
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth size="small" label={t.name} value={name}
                onChange={(e) => { setName(e.target.value); if (error) setError(''); }} required sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label={t.email} type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }} required sx={{ mb: 2 }}
                autoComplete="email"
              />
              <TextField
                fullWidth size="small" label={t.password} type="password" value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }} required sx={{ mb: 0.5 }}
                autoComplete="new-password"
                error={password.length > 0 && !passwordValid}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t.passwordRequirements}
              </Typography>
              <TextField
                fullWidth size="small" label={t.dateOfBirth} type="date" value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)} required sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{t.gender}</InputLabel>
                <Select value={gender} label={t.gender} onChange={(e) => setGender(e.target.value)} required>
                  <MenuItem value="male">{t.male}</MenuItem>
                  <MenuItem value="female">{t.female}</MenuItem>
                  <MenuItem value="other">{t.other}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth size="small" label={t.height} type="number" value={height}
                onChange={(e) => setHeight(e.target.value)} required sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label={t.currentWeight} type="number" value={weight}
                onChange={(e) => setWeight(e.target.value)} required sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label={t.targetWeight} type="number" value={targetWeight}
                onChange={(e) => setTargetWeight(e.target.value)} sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label={t.targetDate} type="date" value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)} sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Button
                type="submit" variant="contained" fullWidth
                disabled={loading || !passwordValid}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : t.createAccount}
              </Button>
            </form>

            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              {t.hasAccount}{' '}
              <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>
                {t.logIn}
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Register;
